import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, NonNullableFormBuilder, ValidatorFn, Validators } from '@angular/forms';
import { catchError, debounceTime, distinctUntilChanged, finalize, map, merge, Observable, of, startWith, Subject, switchMap } from 'rxjs';
import moment from 'moment';
import { BoardNovaPoshtaService } from '../../../services/board-nova-poshta.service';
import { StackedSidebarService } from '../../../services/stacked-sidebar.service';
import { SidebarType } from '../../layout/stacked-sidebar/stacked-sidebar.interface';
import { formatMoneyAmount } from '../../board-v3/price-field.utils';
import {
  NpCabinet,
  NpCargoType,
  NpCodType,
  NpDeliveryType,
  NpPayer,
  NpPaymentMethod,
  NpShipment,
  NpShipmentEstimate,
  NpShipmentPayload,
  NpShipmentPrefill,
  NpWarehouse,
} from '../nova-poshta.types';
import {
  formatNpPhone,
  getVolumetricWeight,
  NP_CARGO_TYPES,
  NP_COD_TYPES,
  NP_DELIVERY_TYPES,
  NP_DESCRIPTION_MAX_LENGTH,
  NP_NOTE_MAX_LENGTH,
  NP_PAYERS,
  NP_PAYMENT_METHODS,
  NP_PHONE_PATTERN,
  normalizeNpPhone,
  NpCityOption,
  toNpCityOption,
  withPicked,
} from '../nova-poshta.utils';

/** Errors are keys of `input_errors`, the way `app-input` shows them */
const notBlank: ValidatorFn = (control) => (String(control.value ?? '').trim() ? null : { incorrect: true });
const npPhone: ValidatorFn = (control) =>
  !control.value || NP_PHONE_PATTERN.test(normalizeNpPhone(control.value)) ? null : { np_phone: true };
const integer: ValidatorFn = (control) =>
  control.value === null || Number.isInteger(control.value) ? null : { integer: true };

/** A postomat takes a parcel or documents up to 20 kg */
const POSTOMAT_CARGO_TYPES: NpCargoType[] = ['Parcel', 'Documents'];
const POSTOMAT_MAX_WEIGHT = 20;

/**
 * Sidebar that creates a Nova Poshta waybill (TTN) of a deal: the cabinet it goes from, the recipient with the city
 * and the branch lookup, the cargo and the payment. The recipient and the amounts come from the deal; the delivery
 * cost is estimated as the form changes. Closes with the created waybill.
 */
@Component({
  selector: 'app-nova-poshta-shipment-form',
  templateUrl: './nova-poshta-shipment-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  host: { class: 'block h-full' },
})
export class NovaPoshtaShipmentFormComponent implements OnInit {
  @Input({ required: true }) cardId!: string;
  @Input() prefill: NpShipmentPrefill | null = null;

  @Output() close = new EventEmitter<NpShipment | void>();

  private readonly npService = inject(BoardNovaPoshtaService);
  private readonly stackedSidebarService = inject(StackedSidebarService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(NonNullableFormBuilder);

  protected readonly deliveryTypes = NP_DELIVERY_TYPES;
  protected readonly cargoTypes = NP_CARGO_TYPES;
  protected readonly payers = NP_PAYERS;
  protected readonly paymentMethods = NP_PAYMENT_METHODS;
  protected readonly codTypes = NP_COD_TYPES;
  protected readonly descriptionMaxLength = NP_DESCRIPTION_MAX_LENGTH;
  protected readonly noteMaxLength = NP_NOTE_MAX_LENGTH;
  protected readonly postomatMaxWeight = POSTOMAT_MAX_WEIGHT;
  protected readonly formatNpPhone = formatNpPhone;
  protected readonly formatMoneyAmount = formatMoneyAmount;
  protected readonly phoneKeys = /[\d+]/;
  protected readonly today = moment().startOf('day').toDate();

  protected readonly recipientForm = this.fb.group({
    name: ['', [Validators.required, notBlank, Validators.maxLength(100)]],
    phone: ['', [Validators.required, npPhone]],
    delivery_type: this.fb.control<NpDeliveryType>('warehouse'),
    city_ref: this.fb.control<string | null>(null, Validators.required),
    warehouse_ref: this.fb.control<string | null>(null, Validators.required),
    street: ['', [Validators.required, notBlank]],
    building: ['', [Validators.required, notBlank]],
    flat: [''],
  });
  protected readonly cargoForm = this.fb.group({
    type: this.fb.control<NpCargoType>('Parcel'),
    description: ['', [Validators.required, notBlank, Validators.maxLength(NP_DESCRIPTION_MAX_LENGTH)]],
    declared_cost: this.fb.control<number | null>(null, [Validators.required, Validators.min(1)]),
    weight: this.fb.control<number | null>(1, [Validators.required, Validators.min(0.1)]),
    seats: this.fb.control<number | null>(1, [Validators.required, Validators.min(1), integer]),
    length: this.fb.control<number | null>(null, Validators.min(1)),
    width: this.fb.control<number | null>(null, Validators.min(1)),
    height: this.fb.control<number | null>(null, Validators.min(1)),
  });
  protected readonly form = this.fb.group({
    cabinet_id: this.fb.control<string | null>(null, Validators.required),
    sender_warehouse_ref: this.fb.control<string | null>(null, Validators.required),
    recipient: this.recipientForm,
    cargo: this.cargoForm,
    payer: this.fb.control<NpPayer>('Recipient'),
    payment_method: this.fb.control<NpPaymentMethod>('Cash'),
    cod_type: this.fb.control<NpCodType>('none'),
    cod_amount: this.fb.control<number | null>(null, [Validators.required, Validators.min(1)]),
    send_date: this.fb.control<Date>(this.today, Validators.required),
    note: ['', Validators.maxLength(NP_NOTE_MAX_LENGTH)],
  });

  protected cabinets: NpCabinet[] = [];
  protected cityOptions: NpCityOption[] = [];
  protected warehouseOptions: NpWarehouse[] = [];
  protected senderWarehouseOptions: NpWarehouse[] = [];
  protected loading = true;
  protected citiesLoading = false;
  protected warehousesLoading = false;
  protected saving = false;
  protected estimate: NpShipmentEstimate | null = null;
  protected estimating = false;
  /** Why there is no cost, the text of Nova Poshta when it refused */
  protected estimateError: string | null = null;

  private readonly citySearch$ = new Subject<string>();
  private readonly warehouseSearch$ = new Subject<string>();
  private readonly senderWarehouseSearch$ = new Subject<string>();
  private pickedCity: NpCityOption | null = null;
  private pickedWarehouse: NpWarehouse | null = null;
  private pickedSenderWarehouse: NpWarehouse | null = null;

  protected get cabinet(): NpCabinet | null {
    return this.cabinets.find((cabinet) => cabinet.id === this.form.controls.cabinet_id.value) ?? null;
  }

  protected get deliveryType(): NpDeliveryType {
    return this.recipientForm.controls.delivery_type.value;
  }

  protected get cargoType(): NpCargoType {
    return this.cargoForm.controls.type.value;
  }

  /** A private person has neither the transfer payment nor the payment control (NovaPay) */
  protected get isPrivateSender(): boolean {
    return this.cabinet?.sender?.type === 'private_person';
  }

  protected isPaymentMethodAllowed(method: NpPaymentMethod): boolean {
    if (method === 'Cash') return this.codType !== 'payment_control';
    return !this.isPrivateSender;
  }

  protected isCodTypeAllowed(type: NpCodType): boolean {
    return type !== 'payment_control' || !this.isPrivateSender;
  }

  protected get codType(): NpCodType {
    return this.form.controls.cod_type.value;
  }

  protected get volumetricWeight(): number {
    const { length, width, height } = this.cargoForm.getRawValue();
    return getVolumetricWeight(length, width, height);
  }

  protected get postomatOverweight(): boolean {
    return this.deliveryType === 'postomat' && (this.cargoForm.controls.weight.value ?? 0) > POSTOMAT_MAX_WEIGHT;
  }

  /** Nova Poshta refuses a parcel to a postomat without the sizes of its seats */
  protected get sizeRequired(): boolean {
    return this.deliveryType === 'postomat' && this.cargoType !== 'Documents';
  }

  protected isCargoTypeAllowed(type: NpCargoType): boolean {
    return this.deliveryType !== 'postomat' || POSTOMAT_CARGO_TYPES.includes(type);
  }

  ngOnInit(): void {
    this.applyPrefill();
    this.toggleControls();
    this.watchLookups();
    this.watchEstimate();

    merge(
      this.recipientForm.controls.delivery_type.valueChanges,
      this.cargoForm.controls.type.valueChanges,
      this.form.controls.cod_type.valueChanges,
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.toggleControls());

    this.npService
      .getCabinets()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.loading = false)),
      )
      .subscribe({
        next: (cabinets) => {
          this.cabinets = cabinets;
          const preferred = cabinets.find((cabinet) => cabinet.is_default) ?? cabinets[0];
          if (preferred) this.form.controls.cabinet_id.setValue(preferred.id);
        },
        error: (error) => this.npService.error(error),
      });
  }

  // #region pickers

  protected setOption<T>(control: FormControl<T>, value: T): void {
    if (control.value === value) return;
    control.setValue(value);
    control.markAsDirty();
  }

  protected onCitySearch(search: string): void {
    this.citySearch$.next(search);
  }

  protected onWarehouseSearch(search: string): void {
    this.warehouseSearch$.next(search);
  }

  protected onSenderWarehouseSearch(search: string): void {
    this.senderWarehouseSearch$.next(search);
  }

  protected onCityPicked(ref: string | null): void {
    this.pickedCity = this.cityOptions.find((city) => city.ref === ref) ?? null;
  }

  protected onWarehousePicked(ref: string | null): void {
    this.pickedWarehouse = this.warehouseOptions.find((warehouse) => warehouse.ref === ref) ?? null;
  }

  protected onSenderWarehousePicked(ref: string | null): void {
    this.pickedSenderWarehouse = this.senderWarehouseOptions.find((warehouse) => warehouse.ref === ref) ?? null;
  }

  protected openSettings(): void {
    this.stackedSidebarService
      .openSidebar({ type: SidebarType.NovaPoshtaSettings, width: '560px' }, {})
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((cabinets?: NpCabinet[]) => {
        if (!cabinets) return;
        this.cabinets = cabinets;
        const picked = this.form.controls.cabinet_id.value;
        if (!cabinets.some((cabinet) => cabinet.id === picked)) {
          this.form.controls.cabinet_id.setValue((cabinets.find((cabinet) => cabinet.is_default) ?? cabinets[0])?.id ?? null);
        } else {
          // The place of sending may have changed there
          this.applySender();
        }
      });
  }

  // #endregion

  protected save(): void {
    if (this.form.invalid || this.saving || this.postomatOverweight) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;
    this.npService
      .createShipment(this.cardId, this.payload())
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: (shipment) => {
          this.npService.success('novaposhta.form.created', { ttn: shipment.ttn_number });
          this.close.emit(shipment);
        },
        error: (error) => this.npService.error(error),
      });
  }

  private payload(): NpShipmentPayload {
    const value = this.form.getRawValue();
    const recipient = value.recipient;
    const cargo = value.cargo;
    const isAddress = recipient.delivery_type === 'address';
    const hasSize = cargo.type !== 'Documents';
    return {
      cabinet_id: value.cabinet_id!,
      sender_warehouse_ref: value.sender_warehouse_ref!,
      recipient: {
        name: recipient.name.trim(),
        phone: normalizeNpPhone(recipient.phone),
        city_ref: recipient.city_ref!,
        delivery_type: recipient.delivery_type,
        warehouse_ref: isAddress ? null : recipient.warehouse_ref,
        street: isAddress ? recipient.street.trim() : null,
        building: isAddress ? recipient.building.trim() : null,
        flat: isAddress ? recipient.flat.trim() || null : null,
      },
      cargo: {
        type: cargo.type,
        description: cargo.description.trim(),
        declared_cost: cargo.declared_cost ?? 0,
        weight: cargo.weight ?? 0,
        seats: cargo.seats ?? 1,
        length: hasSize ? cargo.length : null,
        width: hasSize ? cargo.width : null,
        height: hasSize ? cargo.height : null,
      },
      payer: value.payer,
      payment_method: value.payment_method,
      cod: { type: value.cod_type, amount: value.cod_type === 'none' ? null : value.cod_amount },
      send_date: moment(value.send_date).format('YYYY-MM-DD'),
      note: value.note.trim() || null,
    };
  }

  /** The recipient, the description and the amounts of the deal; the city when Nova Poshta has one by that name */
  private applyPrefill(): void {
    const prefill = this.prefill;
    if (!prefill) return;

    this.recipientForm.patchValue({ name: prefill.recipientName, phone: prefill.recipientPhone });
    this.cargoForm.patchValue({ description: prefill.description, declared_cost: prefill.amount });
    this.form.controls.cod_amount.setValue(prefill.amount);
    if (prefill.amount) this.form.controls.cod_type.setValue('money_transfer');

    const cityName = prefill.cityName?.trim();
    if (!cityName) return;
    this.npService
      .searchCities(cityName)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((cities) => {
        const city = cities.find((item) => item.name.toLocaleLowerCase() === cityName.toLocaleLowerCase());
        if (!city || this.recipientForm.controls.city_ref.value) return;
        this.pickedCity = toNpCityOption(city);
        this.cityOptions = [this.pickedCity];
        this.recipientForm.controls.city_ref.setValue(city.ref);
      });
  }

  /** What is sent depends on the delivery type, the cargo type and the cash on delivery */
  private toggleControls(): void {
    const controls = this.recipientForm.controls;
    const isAddress = this.deliveryType === 'address';
    const toggle = (control: AbstractControl, enabled: boolean) =>
      enabled ? control.enable({ emitEvent: false }) : control.disable({ emitEvent: false });

    toggle(controls.warehouse_ref, !isAddress);
    toggle(controls.street, isAddress);
    toggle(controls.building, isAddress);
    toggle(controls.flat, isAddress);

    const hasSize = this.cargoType !== 'Documents';
    toggle(this.cargoForm.controls.length, hasSize);
    toggle(this.cargoForm.controls.width, hasSize);
    toggle(this.cargoForm.controls.height, hasSize);
    // A postomat takes a parcel only with the sizes of its seats (`OptionsSeat`)
    const sizeValidators = this.sizeRequired ? [Validators.required, Validators.min(1)] : [Validators.min(1)];
    for (const control of [this.cargoForm.controls.length, this.cargoForm.controls.width, this.cargoForm.controls.height]) {
      control.setValidators(sizeValidators);
      control.updateValueAndValidity({ emitEvent: false });
    }

    // A private sender can not take what it has no access to, e.g. after the cabinet changed
    if (!this.isCodTypeAllowed(this.codType)) this.form.controls.cod_type.setValue('money_transfer', { emitEvent: false });
    if (this.isPrivateSender) this.form.controls.payment_method.setValue('Cash', { emitEvent: false });
    toggle(this.form.controls.cod_amount, this.codType !== 'none');
    // Payment control goes to the bank account of the sender: Nova Poshta takes it only paid by card or transfer
    if (this.codType === 'payment_control') this.form.controls.payment_method.setValue('NonCash', { emitEvent: false });

    if (!this.isCargoTypeAllowed(this.cargoType)) this.cargoForm.controls.type.setValue('Parcel', { emitEvent: false });
  }

  /** City and branch search; the branches start over with the city or the delivery type */
  private watchLookups(): void {
    this.citySearch$
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((search) => {
          if (search.trim().length < 2) return of(null);
          this.citiesLoading = true;
          return this.npService.searchCities(search).pipe(
            catchError((error) => this.lookupFailed(error, null)),
            finalize(() => (this.citiesLoading = false)),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((cities) => {
        if (cities) this.cityOptions = withPicked(cities.map(toNpCityOption), this.pickedCity);
      });

    const cityRef = this.recipientForm.controls.city_ref;
    const deliveryType = this.recipientForm.controls.delivery_type;
    merge(cityRef.valueChanges, deliveryType.valueChanges)
      .pipe(
        map(() => `${cityRef.value}|${deliveryType.value}`),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.pickedWarehouse = null;
        this.recipientForm.controls.warehouse_ref.setValue(null);
        this.warehouseSearch$.next('');
      });

    this.warehouseSearch$
      .pipe(
        debounceTime(200),
        switchMap((search) => {
          const ref = cityRef.value;
          if (!ref || deliveryType.value === 'address') return of([]);
          this.warehousesLoading = true;
          const kind = deliveryType.value === 'postomat' ? 'postomat' : 'not_postomat';
          return this.npService.getWarehouses(ref, search, kind).pipe(
            catchError((error) => this.lookupFailed(error, [])),
            finalize(() => (this.warehousesLoading = false)),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((warehouses) => (this.warehouseOptions = withPicked(warehouses, this.pickedWarehouse)));

    this.form.controls.cabinet_id.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.applySender());

    this.senderWarehouseSearch$
      .pipe(
        debounceTime(200),
        switchMap((search) => {
          const ref = this.cabinet?.sender?.city?.ref;
          if (!ref) return of([]);
          return this.npService
            .getWarehouses(ref, search, 'not_postomat')
            .pipe(catchError((error) => this.lookupFailed(error, [])));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((warehouses) => (this.senderWarehouseOptions = withPicked(warehouses, this.pickedSenderWarehouse)));
  }

  /** The sender sends from a branch of the city set with the cabinet, its branch first */
  private applySender(): void {
    const warehouse = this.cabinet?.sender?.warehouse ?? null;
    this.pickedSenderWarehouse = warehouse;
    this.senderWarehouseOptions = warehouse ? [warehouse] : [];
    this.form.controls.sender_warehouse_ref.setValue(warehouse?.ref ?? null);
    this.senderWarehouseSearch$.next('');
    this.toggleControls();
  }

  /** A failed search keeps the next ones going */
  private lookupFailed<T>(error: unknown, fallback: T): Observable<T> {
    this.npService.error(error);
    return of(fallback);
  }

  /** The cost once there is enough to ask for it */
  private watchEstimate(): void {
    this.form.valueChanges
      .pipe(
        startWith(null),
        debounceTime(500),
        map(() => (this.canEstimate() ? this.payload() : null)),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        switchMap((payload) => {
          this.estimateError = null;
          if (!payload) return of(null);
          this.estimating = true;
          return this.npService.estimate(payload).pipe(
            // The error is shown in place of the cost, and the next change asks again
            catchError((error) => {
              this.estimateError = this.npService.errorMessage(error);
              return of(null);
            }),
            finalize(() => (this.estimating = false)),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((estimate) => (this.estimate = estimate));
  }

  private canEstimate(): boolean {
    const { city_ref, delivery_type } = this.recipientForm.getRawValue();
    const { weight, declared_cost } = this.cargoForm.getRawValue();
    return !!this.cabinet?.sender?.city && !!city_ref && !!delivery_type && !!weight && declared_cost !== null;
  }
}
